import { useCallback, useMemo } from 'react';
import { IProduct, ProductID } from '@shared/models/product.model';
import { getProject } from '@client/store/project';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { IProductElement } from '@shared/models/project.model';
import { asArray } from '@shared/helpers/array.helpers';
import {
  getEPDProductId,
  getGenericProductId,
} from '@shared/helpers/product-element.helpers';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { ArrayOrSingle, ItemOrItemId } from '@shared/models/type-helpers.model';
import { getId } from '@shared/helpers/object.helpers';
import { openProductSelector } from './EditProduct/ProductSelector';
import { createLocalStorageRecordStore } from '@client/helpers/local-storage.helpers';
import { useProductsLookup } from '@client/store/product';
import { guards } from '@shared/helpers/typeguards.helpers';

export const {
  getItem: getLocallyStoredEPDIds,
  setItem: setLocallyStoredEPDIds,
  useStoreItem: useLocallyStoredEPDIds,
} = createLocalStorageRecordStore<ProductID, ProductID[]>(
  'epds_applied_to_generic_products',
);

export const addIdToLocallyStoredEPDIds = (
  genericProductId: ProductID,
  epdId: ProductID,
) => {
  const ids = getLocallyStoredEPDIds(genericProductId) ?? [];
  const uniqueIds = new Set([...ids, epdId]);
  setLocallyStoredEPDIds(genericProductId, [...uniqueIds]);
};

/**
 * Store mapping of epds in localstorage based on latest product elements
 * @param productElements
 */
const updateEPDIdsAppliedToGenericProducts = (
  productElements: IProductElement[],
) => {
  for (const element of productElements) {
    const genericProductId = getGenericProductId(element);
    const epdId = getEPDProductId(element);
    // Make sure epd is mapped to the generic product (to reuse conversion factors)
    if (genericProductId && epdId) {
      addIdToLocallyStoredEPDIds(genericProductId, epdId);
    }
  }
};

export const useSelectProductElementEpds = () => {
  const setEpdOnProductElements = useSetEpdOnProductElements();
  return useCallback(
    async (elements: ArrayOrSingle<IProductElement>) => {
      const product = await openProductSelector();
      if (product) {
        await setEpdOnProductElements(asArray(elements), product.id);
      }
      return product;
    },
    [setEpdOnProductElements],
  );
};

/**
 * Get all products that have been applied to the specific generic product
 * @param genericProductId
 */
export const useAppliedEpdsFromGenericProduct = (
  genericProductId: ProductID,
  appliedEpd: IProduct | undefined,
) => {
  const productsLookup = useProductsLookup();
  const localstorageIds = useLocallyStoredEPDIds(genericProductId);

  return useMemo(() => {
    const epdsMappedInLocalstorage: IProduct[] = [];

    for (const id of localstorageIds ?? []) {
      const product = productsLookup[id];

      // epds in localstorage might not be in the lookup
      if (product) epdsMappedInLocalstorage.push(product);
    }

    const epdsMappedInDatabase = Object.values(productsLookup).filter(
      ({ generic_id }) => generic_id === genericProductId,
    );

    // create a map to remove duplicates
    const uniqueMap = new Map(
      [
        ...epdsMappedInLocalstorage,
        ...epdsMappedInDatabase,
        // Always include the active EPD in the list
        appliedEpd,
      ]
        .filter(guards.is.defined)
        .map((product) => [product.id, product]),
    );

    return Array.from(uniqueMap.values());
  }, [productsLookup, genericProductId, appliedEpd, localstorageIds]);
};

/**
 * Clear the EPD on an array of product elements by reverting to the generic product.
 * Creates {@link ProjectChangeType.ProductElementEpd} changes via {@link useApplyProjectChanges}.
 */
export const useClearEpdOnProductElements = () => {
  const applyChanges = useApplyProjectChanges();

  return useCallback(
    async (productElements: IProductElement[]) => {
      const changes: ProjectChange[] = productElements
        .filter((el) => getEPDProductId(el))
        .map((el) => ({
          type: ProjectChangeType.ProductElementEpd as const,
          elementId: el.id,
          productId: getGenericProductId(el) ?? el.product_id,
          genericProductId: undefined,
        }));

      if (changes.length === 0) return getProject();
      return (await applyChanges(changes)).project;
    },
    [applyChanges],
  );
};

/**
 * Set the EPD on the product elements.
 * Creates {@link ProjectChangeType.ProductElementEpd} changes via {@link useApplyProjectChanges}.
 */
export const useSetEpdOnProductElements = () => {
  const applyChanges = useApplyProjectChanges();

  return useCallback(
    async (
      productElements: IProductElement[],
      productOrId: ItemOrItemId<IProduct>,
    ) => {
      const epdId = getId(productOrId);

      const changes: ProjectChange[] = productElements
        .filter((el) => getEPDProductId(el) !== epdId)
        .map((el) => ({
          type: ProjectChangeType.ProductElementEpd as const,
          elementId: el.id,
          productId: epdId,
          genericProductId: getGenericProductId(el),
        }));

      // Store epd → generic mapping in localStorage for reuse
      updateEPDIdsAppliedToGenericProducts(
        productElements.map((el) => ({
          ...el,
          product_id: epdId,
          generic_product_id: getGenericProductId(el),
        })),
      );

      if (changes.length === 0) return getProject();
      return (await applyChanges(changes)).project;
    },
    [applyChanges],
  );
};
